/**
 * Parent-portal self-signup (ADR-0016) — the SINGLE registration door.
 * Slug-less (`/portal/signup`): it creates the GLOBAL account only (no
 * app membership — apps are admin-assigned or auto-enrolled on activation).
 *
 * Two outcomes by the instance registration policy: `direct` mints a token and
 * lands on the launcher (empty unless an app is auto-enroll); `requires_approval`
 * shows an "under review" state (the account is pending until a Tray admin
 * accepts it). When registration is closed, this page bounces to the login.
 *
 * Mirrors `ParentPortalLoginPage` chrome (split hero + card, tenant brand).
 */
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { ApiError } from "@/lib/api";
import { portalApi } from "@/lib/api-portal";
import { parentLauncherPath, parentLoginPath, usePortalAuth } from "@/lib/portalAuth";

/** Readable text colour for a hex background by luminance (legible hero). */
function readableOn(bg: string | undefined): string {
  const m = bg && /^#?([0-9a-fA-F]{6})$/.exec(bg.trim());
  if (!m) return "#ffffff";
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.6 ? "#0f172a" : "#ffffff";
}

export function ParentPortalSignupPage() {
  const navigate = useNavigate();
  const setSession = usePortalAuth((s) => s.setSession);
  const user = usePortalAuth((s) => s.user);

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);

  const brandQ = useQuery({
    queryKey: ["parent-brand"],
    queryFn: () => portalApi.getParentBrand(),
    staleTime: 5 * 60_000,
  });
  const brand = brandQ.data;

  // Already signed in → no point registering.
  if (user) return <Navigate to={parentLauncherPath()} replace />;

  // Registration closed → there is no signup door; send to login.
  if (brand && brand.signup_enabled === false) {
    return <Navigate to={parentLoginPath()} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await portalApi.parentSignup({
        email,
        password,
        full_name: fullName.trim() || undefined,
      });
      if (res.status === "active" && res.access_token && res.user) {
        setSession(res.access_token, res.user, res.refresh_token);
        navigate(parentLauncherPath(), { replace: true });
        return;
      }
      // requires_approval → pending, no token.
      setPending(true);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "error";
      const message = err instanceof ApiError ? err.message : "";
      if (message.includes("account_exists")) {
        setError("An account with this email already exists. Try signing in.");
      } else if (code.includes("validation")) {
        setError("Please check your details and try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  const tenantName = brand?.name || "Portal";
  const accent = brand?.theme.accent || undefined;
  const onAccent = readableOn(accent);
  const pageBg = brand?.theme.background || undefined;
  const inputCls =
    "h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground " +
    "outline-none transition placeholder:text-muted-foreground/70 focus:border-foreground/40 " +
    "focus:ring-2 focus:ring-foreground/10";

  return (
    <div className="tray-light min-h-screen w-full bg-background lg:grid lg:grid-cols-2">
      {/* Branded hero — desktop only. */}
      <div
        className="relative hidden overflow-hidden p-12 lg:flex lg:flex-col lg:justify-between"
        style={{ background: accent || "#0f172a", color: onAccent }}
      >
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -left-16 h-80 w-80 rounded-full bg-black/10 blur-3xl" />
        <div className="relative text-lg font-semibold tracking-tight">{tenantName}</div>
        <div className="relative">
          <h2 className="max-w-md text-4xl font-semibold leading-tight">
            {brand?.login.title || `Join ${tenantName}`}
          </h2>
          <p className="mt-4 max-w-sm text-sm opacity-80">
            {brand?.login.subtitle || "Create an account to get started."}
          </p>
        </div>
        <div className="relative text-xs opacity-60">{brand?.footer || `© ${tenantName}`}</div>
      </div>

      {/* Form column. */}
      <div
        className="flex min-h-screen items-center justify-center p-6"
        style={pageBg ? { background: pageBg } : undefined}
      >
        <div className="portal-pop w-full max-w-md rounded-2xl border border-border bg-surface p-8 shadow-xl">
          {brand?.logo_url ? (
            <img src={brand.logo_url} alt={tenantName} className="mb-6 h-10 max-w-[180px] object-contain" />
          ) : (
            <div className="mb-6 text-lg font-semibold lg:hidden">{tenantName}</div>
          )}

          {pending ? (
            <div className="text-center">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-success/15">
                <CheckCircle2 className="h-6 w-6 text-success" />
              </div>
              <h1 className="mt-4 text-xl font-semibold tracking-tight text-foreground">
                Registration received
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Your account is awaiting approval. You'll be able to sign in once an
                administrator accepts it.
              </p>
              <button
                type="button"
                onClick={() => navigate(parentLoginPath(), { replace: true })}
                className="mt-6 text-sm font-medium text-foreground underline-offset-4 hover:underline"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                Create your account
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Already have one?{" "}
                <button
                  type="button"
                  onClick={() => navigate(parentLoginPath())}
                  className="font-medium text-foreground underline-offset-4 hover:underline"
                >
                  Sign in
                </button>
              </p>

              <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                    Full name
                  </span>
                  <input
                    type="text"
                    autoComplete="name"
                    className={inputCls}
                    placeholder="Jane Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Email</span>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    className={inputCls}
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                    Password
                  </span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className={inputCls}
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </label>

                {error && (
                  <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  style={accent ? { background: accent, color: onAccent } : undefined}
                  className="mt-1 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-accent px-3 text-sm font-semibold text-accent-foreground shadow-sm transition hover:opacity-90 disabled:opacity-60"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {busy ? "Creating…" : "Create account"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
